import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { StatusBadge } from "~/components/status-badge";
import { api } from "~/lib/api";
import { matchesParcel } from "~/lib/ops-search";
import { cn } from "~/lib/utils";

const MAX_RESULTS = 8;

const OpenSearchContext = createContext<() => void>(() => {});

export function useOpenSearch(): () => void {
  return useContext(OpenSearchContext);
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const openSearch = useCallback(() => setOpen(true), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const current = useQuery({
    queryKey: ["ops", "parcels", "counts"],
    queryFn: () => api.ops.listParcels({}),
    enabled: open,
  });
  const delivered = useQuery({
    queryKey: ["ops", "parcels", "delivered"],
    queryFn: () => api.ops.listParcels({ archived: true }),
    enabled: open,
  });

  const term = query.trim();
  const results = useMemo(() => {
    if (!term) return [];
    return [...(current.data ?? []), ...(delivered.data ?? [])].filter((p) => matchesParcel(p, term)).slice(0, MAX_RESULTS);
  }, [current.data, delivered.data, term]);

  useEffect(() => setActive(0), [term, open]);

  function choose(id?: string | null) {
    if (!id) return;
    setOpen(false);
    setQuery("");
    void navigate({ to: "/ops/parcels", search: { parcel: id } });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(results[active]?.id);
    }
  }

  const searching = current.isLoading || delivered.isLoading;

  return (
    <OpenSearchContext.Provider value={openSearch}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent showCloseButton={false} className="top-[18%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-[560px]">
          <DialogTitle className="sr-only">Find a customer or parcel</DialogTitle>
          <div className="flex items-center gap-2.5 px-4">
            <Search className="text-text-faint size-4 shrink-0" strokeWidth={1.75} />
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Find a customer or parcel"
              placeholder="Name, phone, tracking number, customer code or parcel ref"
              className="placeholder:text-text-faint h-12 min-w-0 flex-1 bg-transparent text-[15px] outline-none max-md:text-[16px]"
            />
          </div>
          <div className="border-hairline max-h-[360px] overflow-y-auto border-t">
            {!term ? (
              <p className="text-text-faint p-6 text-center text-[13px]">
                Searches every parcel, including delivered ones. A phone number works in any format.
              </p>
            ) : results.length === 0 ? (
              <p className="text-text-faint p-6 text-center text-[13px]">
                {searching ? "Searching…" : `Nothing matches "${term}". Check the number or try the customer's name.`}
              </p>
            ) : (
              results.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => choose(p.id)}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    "flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
                    i === active ? "bg-accent" : "hover:bg-accent/50",
                  )}
                >
                  <div className="min-w-0 flex-1 leading-tight">
                    <p className="truncate text-[13px] font-medium">
                      {p.customerName || "No name"} · {p.description || "Parcel"}
                    </p>
                    <p className="text-text-faint truncate text-[12px]">
                      {[p.customerPhone, p.trackingNumber, p.labelCode && `Ref ${p.labelCode}`].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <StatusBadge status={p.status} />
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </OpenSearchContext.Provider>
  );
}
